/**
 * Multi-Language Configuration
 * Defines supported languages and their properties
 */

export type Locale = 'en' | 'bn' | 'ar';

export interface LanguageConfig {
  code: Locale;
  name: string;
  nativeName: string;
  direction: 'ltr' | 'rtl';
  flag: string;
  currency: string;
}

/**
 * Supported languages configuration
 */
export const languages: Record<Locale, LanguageConfig> = {
  en: {
    code: 'en',
    name: 'English',
    nativeName: 'English',
    direction: 'ltr',
    flag: '🇬🇧',
    currency: 'GBP',
  },
  bn: {
    code: 'bn',
    name: 'Bengali',
    nativeName: 'বাংলা',
    direction: 'ltr',
    flag: '🇧🇩',
    currency: 'BDT',
  },
  ar: {
    code: 'ar',
    name: 'Arabic',
    nativeName: 'العربية',
    direction: 'rtl',
    flag: '🇸🇦',
    currency: 'SAR',
  },
};

/**
 * Array of all supported locale codes
 */
export const locales: Locale[] = ['en', 'bn', 'ar'];

/**
 * Default locale for the platform
 */
export const defaultLocale: Locale = 'bn';

/**
 * Fallback chain for translations
 * If content not available in requested language, try these in order
 */
export const fallbackLocales: Record<Locale, Locale[]> = {
  en: ['en'],
  bn: ['bn', 'en'],
  ar: ['ar', 'en'],
};

/**
 * Check if a locale code is valid
 */
export function isValidLocale(locale: string): locale is Locale {
  return locales.includes(locale as Locale);
}

/**
 * Get language configuration for a locale
 */
export function getLanguageConfig(locale: Locale): LanguageConfig {
  return languages[locale] || languages[defaultLocale];
}

/**
 * Get text direction for a locale
 */
export function getTextDirection(locale: Locale): 'ltr' | 'rtl' {
  return languages[locale]?.direction || 'ltr';
}

/**
 * Get currency for a locale
 */
export function getCurrency(locale: Locale): string {
  return languages[locale]?.currency || 'GBP';
}
